/**
 * useEntitlements - Slice 2.4 (2026-05-20).
 *
 * Hook that fetches the current tenant's entitlements from the backend
 * and exposes a `hasModule(name)` helper. Used by SideNav to filter
 * NAV groups based on what the tenant actually bought.
 *
 * Per docs/architecture/15 §22 - entitlements separate from feature flags.
 * The shell's nav graph is the OneTrust-pattern rendering surface: a
 * DSPM-only tenant sees no Activity/Detection items even if their
 * feature flags are flipped on.
 */

import { useCallback, useEffect, useState } from 'react';

import { apiFetch, getAuthToken } from './api';
import type { ModuleLicence } from './licence-notices';

export interface Entitlements {
  sku: string;
  modules: string[];
  /**
   * Per-module licence standing, as the API's route gate judges it now: in
   * term, grace, restricted (read only) or refused. Absent from an older API.
   * Rendered by LicenceStatusNotice in the side nav.
   */
  licences?: ModuleLicence[];
  billingStatus: 'active' | 'trial' | 'demo' | 'suspended' | 'cancelled';
  trialModules: string[];
  trialEndsAt: string | null;
  /**
   * Tenant identity for the shell TopBar (Phase 0 item 1, 2026-05-26).
   * Lets the TopBar render the real tenant name + tier instead of the
   * previously-hardcoded "Acme Bank Ltd · Sovereign · BFSI". Null fields
   * when the tenant row is incomplete (defensive - should exist for any
   * provisioned tenant).
   */
  tenant?: {
    id: string;
    slug: string | null;
    displayName: string | null;
    tier: string | null;
    industry: string | null;
    /**
     * Privacy laws the tenant is subject to (tenants.regulations). Absent from
     * an older API and empty for an unmirrored tenant; both read as DPDPA in
     * regulation-terms.ts.
     */
    regulations?: string[];
  };
}

interface UseEntitlementsResult {
  loading: boolean;
  error: string | null;
  entitlements: Entitlements | null;
  hasModule: (module: string) => boolean;
  hasAnyModule: (modules: ReadonlyArray<string>) => boolean;
  refresh: () => Promise<void>;
}

interface EntitlementsResponse {
  ok: boolean;
  data: Entitlements;
}

// Cache across hook instances (component remount shouldn't re-fetch).
let _cache: Entitlements | null = null;
let _cacheAt = 0;
// Every labelled screen asks for the tenant's regulations on first paint, so
// concurrent mounts share one request instead of each sending their own.
let _inflight: Promise<Entitlements> | null = null;
const CACHE_TTL_MS = 60_000;

export function useEntitlements(): UseEntitlementsResult {
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    data: Entitlements | null;
  }>(() => ({
    loading: !_cache,
    error: null,
    data: _cache,
  }));

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      // Not logged in - return null entitlements; surfaces stay hidden.
      setState({ loading: false, error: null, data: null });
      return;
    }
    try {
      _inflight ??= apiFetch<EntitlementsResponse>('/v1/entitlements/current')
        .then((res) => {
          _cache = res.data;
          _cacheAt = Date.now();
          return res.data;
        })
        .finally(() => {
          _inflight = null;
        });
      const data = await _inflight;
      setState({ loading: false, error: null, data });
    } catch (err) {
      const msg = (err as Error).message ?? 'Failed to load entitlements';
      // FAIL CLOSED (release 3.1, persona D6). This used to fall back to
      // showing every module when the call failed, so a tenant whose licences
      // could not be read was offered every module's pages, each of which then
      // answered 402. Unknown licences now offer none; the route guard says the
      // licences could not be checked and offers a retry.
      setState({ loading: false, error: msg, data: null });
    }
  }, []);

  useEffect(() => {
    if (_cache && Date.now() - _cacheAt < CACHE_TTL_MS) {
      setState({ loading: false, error: null, data: _cache });
      return;
    }
    void refresh();
  }, [refresh]);

  const hasModule = useCallback(
    (module: string): boolean => {
      if (!state.data) {
        // Loading, failed, or signed out: no module (fail closed, release 3.1 D6).
        return false;
      }
      if (state.data.billingStatus === 'cancelled') return false;
      if (state.data.modules.includes(module)) return true;
      if (
        state.data.trialModules.includes(module) &&
        state.data.trialEndsAt &&
        new Date(state.data.trialEndsAt) > new Date()
      ) {
        return true;
      }
      return false;
    },
    [state.data],
  );

  const hasAnyModule = useCallback(
    (modules: ReadonlyArray<string>): boolean => {
      return modules.some((m) => hasModule(m));
    },
    [hasModule],
  );

  return {
    loading: state.loading,
    error: state.error,
    entitlements: state.data,
    hasModule,
    hasAnyModule,
    refresh,
  };
}
